import { resolveConfiguredZoneLabel } from '../customerLabels/customerFacingControlLabel';

export type ContributingConditionStatus =
  | 'hypothesis'
  | 'confirmed'
  | 'inconclusive'
  | 'not_supported';

export type InputNeededFrom =
  | 'ehs'
  | 'operations'
  | 'supervisor'
  | 'facilities'
  | 'maintenance'
  | 'worker_representative'
  | 'unknown';

export const CONTRIBUTING_CONDITION_STATUS_OPTIONS: Array<{
  value: ContributingConditionStatus;
  label: string;
}> = [
  { value: 'hypothesis', label: 'Reviewer hypothesis' },
  { value: 'confirmed', label: 'Confirmed contributor' },
  { value: 'inconclusive', label: 'Inconclusive' },
  { value: 'not_supported', label: 'Not supported' },
];

export const INPUT_NEEDED_FROM_OPTIONS: Array<{
  value: InputNeededFrom;
  label: string;
}> = [
  { value: 'ehs', label: 'EHS' },
  { value: 'operations', label: 'Operations' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'facilities', label: 'Facilities' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'worker_representative', label: 'Worker representative' },
  { value: 'unknown', label: 'Unknown / needs review' },
];

export interface WorkplaceConditionFields {
  condition_type?: string | null;
  condition_summary?: string | null;
  likely_contributing_condition?: string | null;
  contributing_condition_status?: ContributingConditionStatus | null;
  condition_tags?: string[] | null;
  reviewer_notes?: string | null;
  input_needed_from?: string | null;
  why_this_matters_text?: string | null;
  worker_support_note_enabled?: boolean | null;
}

export function readWorkplaceCondition(scope: unknown): WorkplaceConditionFields {
  if (!scope || typeof scope !== 'object') return {};
  const workplaceCondition = (scope as Record<string, unknown>).workplace_condition;
  if (!workplaceCondition || typeof workplaceCondition !== 'object') return {};
  return { ...(workplaceCondition as WorkplaceConditionFields) };
}

export function resolveContributingConditionStatusLabel(
  value?: string | null,
): string | null {
  const key = String(value || '').trim() as ContributingConditionStatus;
  const match = CONTRIBUTING_CONDITION_STATUS_OPTIONS.find((option) => option.value === key);
  return match?.label || null;
}

export function resolveInputNeededFromLabel(value?: string | null): string | null {
  const key = String(value || '').trim() as InputNeededFrom;
  const match = INPUT_NEEDED_FROM_OPTIONS.find((option) => option.value === key);
  return match?.label || null;
}

export function buildContributingConditionDisplayLine(
  likelyContributing?: string | null,
  status?: ContributingConditionStatus | string | null,
): string | null {
  const text = String(likelyContributing || '').trim();
  const normalizedStatus = String(status || '').trim() as ContributingConditionStatus;
  const effectiveStatus = normalizedStatus || (text ? 'hypothesis' : '');

  if (!effectiveStatus && !text) return null;

  switch (effectiveStatus) {
    case 'confirmed':
      return text ? `Confirmed contributor: ${text}` : 'Confirmed contributor';
    case 'inconclusive':
      return 'Contributing condition review: inconclusive.';
    case 'not_supported':
      return 'Contributing condition review: not supported by current evidence.';
    case 'hypothesis':
      return text ? `Reviewer hypothesis: ${text}` : null;
    default:
      return text ? `Reviewer hypothesis: ${text}` : null;
  }
}

export function hasContributingConditionReview(fields: WorkplaceConditionFields): boolean {
  return Boolean(
    String(fields.likely_contributing_condition || '').trim()
    || String(fields.contributing_condition_status || '').trim()
    || String(fields.reviewer_notes || '').trim()
    || String(fields.input_needed_from || '').trim(),
  );
}

function normalizeRiskTokens(scope: Record<string, unknown>): string {
  return [
    scope.risk_type,
    scope.hazard_category,
    scope.incident_type,
  ]
    .map((value) => String(value || '').trim().toLowerCase())
    .filter(Boolean)
    .join(' ');
}

function buyerFacingZoneLabel(value: string): string {
  const raw = value.trim();
  if (!raw) return raw;
  if (/\s/.test(raw)) return raw;
  return resolveConfiguredZoneLabel(raw) || raw;
}

function firstZoneLabel(scope: Record<string, unknown>): string {
  const zones = Array.isArray(scope.zones) ? scope.zones : [];
  for (const zone of zones) {
    const label = String(zone || '').trim();
    if (label) return buyerFacingZoneLabel(label);
  }
  const zoneId = String(scope.zone_id || '').trim();
  return buyerFacingZoneLabel(zoneId) || 'the observed zone';
}

export function formatObservedAcrossScans(count: number): string | null {
  const n = Math.trunc(Number(count));
  if (!Number.isFinite(n) || n < 1) return null;
  if (n === 1) return '1 Safety Scan on this Case';
  return `Observed across ${n} Safety Scans`;
}

export function buildDefaultConditionSummary(input: {
  scope?: Record<string, unknown> | null;
}): string {
  const scope = input.scope && typeof input.scope === 'object' ? input.scope : {};
  const combined = normalizeRiskTokens(scope);
  const pattern = /forklift|pedestrian|vehicle/.test(combined)
    ? 'Repeated vehicle-pedestrian interaction'
    : 'Repeated exposure';
  return `${pattern} was observed in ${firstZoneLabel(scope)} during the scan window.`;
}

export function hasWorkplaceConditionContent(fields: WorkplaceConditionFields): boolean {
  return Boolean(
    String(fields.condition_type || '').trim()
    || String(fields.condition_summary || '').trim()
    || String(fields.likely_contributing_condition || '').trim()
    || String(fields.why_this_matters_text || '').trim()
    || (Array.isArray(fields.condition_tags) && fields.condition_tags.length > 0)
    || hasContributingConditionReview(fields),
  );
}
